/**
 * @deprecated Use Central's entitlementsEffective for org plan gating. Local org plan tiers are no longer authoritative.
 *
 * Organization Plan Policies Configuration
 *
 * Defines numeric limits for organization-level plans.
 * These apply at the org level, not individual user level.
 */

import { limitLabels, limitReachedMessage } from './limitCopy.js';

export interface OrgPlanLimits {
  // Team size
  maxMembers: number;

  // Training system
  maxTrainingModules: number;
  maxAssignmentsPerMember: number;
  maxModuleLessons: number;

  // Shared content
  maxSharedContentSets: number;
  maxSharedContentItemsPerSet: number;

  // RAG / Organization knowledge base
  maxOrgRagDocuments: number;
  maxOrgRagStorageMb: number;

  // Analytics
  hasOrgAnalytics: boolean;
  hasCoachingAnalytics: boolean;
  hasTrainingAnalytics: boolean;

  // Advanced features
  hasCustomBranding: boolean;
  hasSsoIntegration: boolean;
  hasApiAccess: boolean;
}

/**
 * Organization plan policies keyed by org plan key.
 */
export const ORG_PLAN_POLICIES: Record<string, OrgPlanLimits> = {
  // Basic org tier - Small teams
  org_basic: {
    maxMembers: 10,
    maxTrainingModules: 10,
    maxAssignmentsPerMember: 5,
    maxModuleLessons: 20,
    maxSharedContentSets: 5,
    maxSharedContentItemsPerSet: 50,
    maxOrgRagDocuments: 20,
    maxOrgRagStorageMb: 100,
    hasOrgAnalytics: true,
    hasCoachingAnalytics: false,
    hasTrainingAnalytics: true,
    hasCustomBranding: false,
    hasSsoIntegration: false,
    hasApiAccess: false,
  },

  // Pro org tier - Growing teams
  org_pro: {
    maxMembers: 50,
    maxTrainingModules: 50,
    maxAssignmentsPerMember: 20,
    maxModuleLessons: 50,
    maxSharedContentSets: 20,
    maxSharedContentItemsPerSet: 100,
    maxOrgRagDocuments: 100,
    maxOrgRagStorageMb: 500,
    hasOrgAnalytics: true,
    hasCoachingAnalytics: true,
    hasTrainingAnalytics: true,
    hasCustomBranding: true,
    hasSsoIntegration: false,
    hasApiAccess: true,
  },

  // Enterprise org tier - Large organizations
  org_enterprise: {
    maxMembers: 500,
    maxTrainingModules: 200,
    maxAssignmentsPerMember: 50,
    maxModuleLessons: 100,
    maxSharedContentSets: 100,
    maxSharedContentItemsPerSet: 500,
    maxOrgRagDocuments: 1000,
    maxOrgRagStorageMb: 5000,
    hasOrgAnalytics: true,
    hasCoachingAnalytics: true,
    hasTrainingAnalytics: true,
    hasCustomBranding: true,
    hasSsoIntegration: true,
    hasApiAccess: true,
  },
};

/**
 * Default org plan for new organizations.
 */
export const DEFAULT_ORG_PLAN = 'org_basic';

/**
 * Get org plan limits for a given org plan key.
 * Falls back to org_basic if plan not found.
 */
export function getOrgPlanLimits(orgPlanKey: string | null | undefined): OrgPlanLimits {
  if (!orgPlanKey) return ORG_PLAN_POLICIES[DEFAULT_ORG_PLAN];
  return ORG_PLAN_POLICIES[orgPlanKey] ?? ORG_PLAN_POLICIES[DEFAULT_ORG_PLAN];
}

/**
 * Human-readable limit names for org error messages, from the one limit copy
 * (config/limitCopy.ts). maxTrainingModules reads "active training modules".
 */
export const ORG_LIMIT_KEY_LABELS: Record<string, string> = limitLabels('org');

/**
 * Get display name for an org plan.
 */
export function getOrgPlanDisplayName(orgPlanKey: string): string {
  switch (orgPlanKey) {
    case 'org_basic':
      return 'Team Basic';
    case 'org_pro':
      return 'Team Pro';
    case 'org_enterprise':
      return 'Enterprise';
    default:
      return 'Team Basic';
  }
}

/**
 * The refusal when one more ACTIVE training module (create / restore) would
 * exceed maxTrainingModules. Archived modules hold no capacity. Worded by the
 * one limit copy (config/limitCopy.ts).
 */
export function trainingModuleLimitMessage(
  maxValue: number,
  orgPlanKey: string,
  action: 'create' | 'restore',
  currentValue: number = maxValue
): string {
  return limitReachedMessage({
    key: 'maxTrainingModules',
    max: maxValue,
    current: currentValue,
    planName: getOrgPlanDisplayName(orgPlanKey),
    action,
  });
}
